import { z } from 'zod';

import type {
  JsonSchema,
  JsonSchemaProperty,
  JsonSchemaDependency,
} from '../types';

// ---------------------------------------------------------------------------
// Build a single Zod field from a JSON Schema property
// ---------------------------------------------------------------------------

function buildZodField(
  prop: JsonSchemaProperty,
  isRequired: boolean,
): z.ZodTypeAny {
  switch (prop.type) {
    case 'boolean': {
      const schema = z.boolean();
      return isRequired ? schema : schema.optional();
    }

    case 'number':
    case 'integer': {
      let schema = z.coerce.number({
        error: 'Must be a number',
      });

      if (prop.type === 'integer')
        schema = schema.int('Must be a whole number');
      if (prop.minimum !== undefined)
        schema = schema.min(prop.minimum, `Minimum value is ${prop.minimum}`);
      if (prop.maximum !== undefined)
        schema = schema.max(prop.maximum, `Maximum value is ${prop.maximum}`);
      if (prop.exclusiveMinimum !== undefined)
        schema = schema.gt(
          prop.exclusiveMinimum,
          `Must be greater than ${prop.exclusiveMinimum}`,
        );
      if (prop.exclusiveMaximum !== undefined)
        schema = schema.lt(
          prop.exclusiveMaximum,
          `Must be less than ${prop.exclusiveMaximum}`,
        );
      if (prop.multipleOf !== undefined)
        schema = schema.multipleOf(prop.multipleOf);

      return isRequired ? schema : schema.optional();
    }

    case 'string':
    default: {
      // String with enum -> z.enum
      if (prop.enum && prop.enum.length > 0) {
        const enumSchema = z.enum(prop.enum as [string, ...string[]]);
        if (isRequired) return enumSchema;
        return enumSchema.optional().or(z.literal(''));
      }

      // Plain string
      let schema = z.string();

      if (prop.format === 'email') {
        schema = schema.email('Enter a valid email address');
      }
      if (prop.minLength !== undefined) {
        schema = schema.min(
          prop.minLength,
          `Minimum ${prop.minLength} characters`,
        );
      }
      if (prop.maxLength !== undefined) {
        schema = schema.max(
          prop.maxLength,
          `Maximum ${prop.maxLength} characters`,
        );
      }
      if (prop.pattern) {
        schema = schema.regex(new RegExp(prop.pattern), 'Invalid format');
      }

      if (isRequired) {
        return schema.min(1, `Required`);
      }
      return schema.optional().or(z.literal(''));
    }
  }
}

// ---------------------------------------------------------------------------
// Collect all dependency-conditional fields and their required-when rules
// ---------------------------------------------------------------------------

interface ConditionalRequirement {
  parentField: string;
  parentValues: string[];
  childField: string;
}

function collectConditionalRequirements(
  dependencies: Record<string, JsonSchemaDependency>,
): ConditionalRequirement[] {
  const requirements: ConditionalRequirement[] = [];

  for (const [parentField, dep] of Object.entries(dependencies)) {
    if (dep.oneOf) {
      for (const branch of dep.oneOf) {
        const parentEnum = branch.properties?.[parentField]?.enum;
        const requiredChildren = branch.required ?? [];

        for (const childField of requiredChildren) {
          if (childField === parentField) continue;
          if (parentEnum && parentEnum.length > 0) {
            requirements.push({
              parentField,
              parentValues: parentEnum,
              childField,
            });
          }
        }
      }
    }

    // Any-value dependency
    if (dep.properties && dep.required) {
      for (const childField of dep.required) {
        if (childField === parentField) continue;
        requirements.push({
          parentField,
          parentValues: [], // empty means "any value"
          childField,
        });
      }
    }
  }

  return requirements;
}

// ---------------------------------------------------------------------------
// Build the full Zod object schema from a JSON Schema
// ---------------------------------------------------------------------------

export function buildZodSchema(schema: JsonSchema): z.ZodType {
  const shape: Record<string, z.ZodTypeAny> = {};
  const requiredFields = new Set(schema.required ?? []);

  // Collect conditional fields so we can make them optional in the base schema
  const conditionalRequirements = schema.dependencies
    ? collectConditionalRequirements(schema.dependencies)
    : [];
  const conditionalFieldNames = new Set(
    conditionalRequirements.map((r) => r.childField),
  );

  // Also collect any properties defined only inside dependency branches
  const dependencyProperties: Record<string, JsonSchemaProperty> = {};
  if (schema.dependencies) {
    for (const dep of Object.values(schema.dependencies)) {
      if (dep.oneOf) {
        for (const branch of dep.oneOf) {
          for (const [key, prop] of Object.entries(branch.properties ?? {})) {
            if (!schema.properties[key] && prop.type) {
              dependencyProperties[key] = prop as JsonSchemaProperty;
            }
          }
        }
      }
      if (dep.properties) {
        for (const [key, prop] of Object.entries(dep.properties)) {
          if (!schema.properties[key] && prop.type) {
            dependencyProperties[key] = prop as JsonSchemaProperty;
          }
        }
      }
    }
  }

  // Build shape from top-level properties
  for (const [name, prop] of Object.entries(schema.properties)) {
    const isConditional = conditionalFieldNames.has(name);
    const isRequired = !isConditional && requiredFields.has(name);
    shape[name] = buildZodField(prop, isRequired);

    // If it's a conditional field, always make it optional in the base schema
    if (isConditional) {
      shape[name] = buildZodField(prop, false);
    }
  }

  // Add dependency-only properties
  for (const [name, prop] of Object.entries(dependencyProperties)) {
    shape[name] = buildZodField(prop, false);
  }

  const baseSchema = z.object(shape);

  // If there are conditional requirements, add superRefine to enforce them
  if (conditionalRequirements.length > 0) {
    return baseSchema.superRefine((data, ctx) => {
      for (const req of conditionalRequirements) {
        const parentValue = data[req.parentField];
        const childValue = data[req.childField];

        const isVisible =
          req.parentValues.length === 0
            ? parentValue !== undefined && parentValue !== ''
            : req.parentValues.includes(parentValue as string);

        if (isVisible && (childValue === undefined || childValue === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: [req.childField],
          });
        }
      }
    });
  }

  return baseSchema;
}
